import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { JwtLegFiClaims } from './auth/jwt-legfi-claims.model';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { Routes } from '../config/routes';
import { ApplicationHttpClient } from '../components/shared/http/application-http-client';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { UnifiedBankAccount } from '../models/entities/unified-bank-account';
import { GrowlerService } from '../components/shared/growler/growler.service';
import { PlaidToken } from '../models/entities/plaid-token';
import { PlaidTokenService } from './plaid-token.service';
import { BankAccountsService } from './financial-accounts/bank-accounts.service';

declare var Plaid: any;

export interface PlaidSuccessMetaData
{
    institution: {
        name: string;
        institution_id: string;
    };
    account_id: string;
    account: {
        id: string;
        name: string;
        mask: string;
        type: string;
        subtype: string;
        verification_status: string;
    };
    accounts: {
        id: string;
        name: string;
        mask: string;
        type: string;
        subtype: string;
        verification_status: string;
    }[];
    linkSessionId: string;
    public_token: string;
}

export interface PlaidLinkResult
{
    success: boolean;
    publicToken?: string;
    metadata?: PlaidSuccessMetaData;
}

export interface PlaidInstitutionSearchResult
{
    countryCodes: string[];
    credentials: {
        label: string; name: string; type: string;
    }[];
    hasMfa: boolean;
    inputSpec: string;
    institutionId: string;
    mfa: string[];
    mfaCodeType: string;
    name: string;
    oauth: boolean;
    products: string[];
    routingNumbers: string[];
}

/**
 * SD-11604: the one field the relink prompt and relaunch read, so the Financial Accounts card's
 * `ConnectedBankAccount` can use the same prompt and flow as the `UnifiedBankAccount` surfaces.
 */
export type PlaidRelinkableAccount = Pick<UnifiedBankAccount, 'plaidToken'>

/**
 * SD-12080 AC4: a FRESH link also needs the row's id, because the new token has to be
 * attached to it. A relink (update mode) does not — it repairs the token in place.
 */
export type FreshLinkableAccount = Pick<UnifiedBankAccount, 'id' | 'plaidToken'> & { last4?: string };

/**
 * One bank-account row that was attached to the token being replaced. Review (Anthropic) A2:
 * a PlaidToken can back SEVERAL rows — one login, an operating and a reserve account — so a
 * fresh link has to carry all of them, not just the row whose button was clicked.
 */
interface FreshLinkRow
{
    id: number;
    last4: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class PlaidService
{
    private plaidObject: any = null;

    constructor(
            private _http: ApplicationHttpClient,
            private _growler: GrowlerService,
            private _plaidTokenService: PlaidTokenService,
            private _bankAccounts: BankAccountsService,
    ) {
    }

    /**
     * The prompt copy, so a v2 surface asking the same question through a v2 dialog cannot reword
     * it. Same reasoning as the balance-CTA helpers: share the sentence, not a second copy of it.
     */
    plaidReconnectPrompt(account: PlaidRelinkableAccount): { title: string; content: string } {
        const loginRequired = account.plaidToken.plaidError.errorCode === 'ITEM_LOGIN_REQUIRED';

        return {
            title: loginRequired ? 'Plaid Login Required' : 'Plaid Error',
            content: loginRequired
                ? 'The login details for this connection have changed (password, MFA) and a user login is required to restore connectivity. Transaction imports have been paused. Click "Continue to Plaid" below to login and restore connectivity to your accounts.'
                : account.plaidToken.plaidError.errorMessage,
        };
    }

    /**
     * The relink itself, once the caller has confirmed. Public so a surface with its own
     * confirmation dialog does not have to reimplement the token fetch and Plaid handoff.
     */
    relaunchPlaidLink(account: PlaidRelinkableAccount, onSuccess: () => void): void {
        // The bank-accounts list response no longer includes updateLinkToken (SD-6302 removed it as
        // a slow accessor), so fetch a fresh token only once the user has committed to relinking.
        this._plaidTokenService.getToken(account.plaidToken.id).subscribe({
            next: (token: PlaidToken) => this.openPlaidLink(token, onSuccess),
            error: () => this._growler.oops(
                    'We were unable to reach Plaid to start the reconnection. Please try again in a few minutes.',
            ),
        });
    }

    /**
     * SD-12080 AC#4 — "on a Plaid account it starts a FRESH Plaid link AFTER THE TOKEN IS CLEARED".
     *
     * ⚠️ THIS IS NOT `relaunchPlaidLink()` AND THE DIFFERENCE IS THE WHOLE POINT. That one runs
     * Plaid in UPDATE mode off `token.updateLinkToken`, which repairs the EXISTING item. AC#4 asks
     * for the broken item to be discarded and a new one established, which is the only remedy when
     * the item itself is the problem rather than the credentials on it.
     *
     * ⚠️ ORDER IS LOAD-BEARING AND THE TOKEN IS CLEARED FIRST, as the AC words it. If the fresh
     * link were opened first and the delete ran afterwards, a user who abandoned Plaid's modal
     * would be left with the broken token still attached AND a new one created — two logins for
     * one bank, which is the duplicate-connection mess SD-9584/SD-9347 describe from the other
     * direction.
     *
     * ⚠️ AND "CLEARED" IS THREE STEPS, NOT ONE: read the token, DETACH every row on it, then
     * delete. An earlier version of this method deleted without detaching, and the claim that an
     * abandoned flow "leaves the account simply unlinked" was then FALSE — the row kept
     * `plaid_token_id` on a soft-deleted token, so `$uba->plaidToken` resolved NULL while the row
     * still reported `isBalanceLinked`, and the card offered neither a CTA nor a warning. The feed
     * was dead permanently and invisibly. Detaching first is what makes the sentence true.
     *
     * A delete that fails stops the journey rather than opening Plaid anyway: re-linking over a
     * token we failed to remove is how the duplicate arises.
     */
    startFreshPlaidLink(account: FreshLinkableAccount, onSuccess: () => void, onSettled: () => void = () => undefined): void {
        const plaidTokenId = account?.plaidToken?.id;
        const unifiedBankAccountId = account?.id;

        // ⚠️ THE ACCOUNT ID IS REQUIRED, NOT OPTIONAL. Without it the row cannot be repointed at the
        // new token, and an unattached row is WORSE than no relink: the old token is already
        // soft-deleted and its Plaid item destroyed, so the feed would be dead permanently.
        if (!unifiedBankAccountId) {
            this._growler.oops('We could not identify the account to reconnect. Please refresh and try again.');
            onSettled();

            return;
        }

        if (!plaidTokenId) {
            this._growler.oops('We could not find the bank connection to replace. Please refresh and try again.');
            onSettled();

            return;
        }

        // The token is READ before it is destroyed, because destroying it is the only moment at
        // which its membership is still knowable. `PlaidTokenController@show` eager-loads
        // `unifiedBankAccounts` and appends `plaid_accounts`, so both halves of the repoint — who
        // was on the old login, and what the new login offers — come from real payload rather than
        // assumption.
        this._plaidTokenService.getToken(plaidTokenId).pipe(
            catchError(() => {
                this._growler.oops('We were unable to read the bank connection to replace. Please try again in a few minutes.');
                onSettled();

                return EMPTY;
            }),
            switchMap((oldToken: PlaidToken) => {
                const rows = this._rowsOnToken(oldToken, unifiedBankAccountId, account?.last4 ?? null);

                // ⚠️ DETACH BEFORE DELETE — review (Anthropic) A6. The delete soft-deletes the
                // token, and a row still carrying `plaid_token_id` then names a TRASHED row:
                // `$uba->plaidToken` resolves NULL while the row keeps reporting
                // `isBalanceLinked`, so neither page offers a CTA or a warning and the feed is
                // dead silently and permanently. Detaching first means an abandoned modal — the
                // most likely ending — leaves every affected row honestly unlinked, which the
                // card already renders as "Link".
                return forkJoin(rows.map((row: FreshLinkRow) => this._bankAccounts.updateUnifiedAccount(row.id, {
                    plaidTokenId: null,
                    plaidAccountId: null,
                }))).pipe(
                    map(() => rows),
                    catchError(() => {
                        this._growler.oops(
                                'We were unable to clear the old bank connection, so the new one was not started. Please try again in a few minutes.',
                        );
                        onSettled();

                        return EMPTY;
                    }),
                );
            }),
            switchMap((rows: FreshLinkRow[]) => this._plaidTokenService.deleteToken(plaidTokenId).pipe(
                map(() => rows),
                catchError(() => {
                    this._growler.oops(
                            'We were unable to remove the old bank connection, so the new one was not started. Please try again in a few minutes.',
                    );
                    onSettled();

                    return EMPTY;
                }),
            )),
            switchMap((rows: FreshLinkRow[]) => (this.getLinkToken() as Observable<string>).pipe(
                map((linkToken: string) => ({ rows, linkToken })),
                catchError(() => {
                    this._growler.oops('We were unable to reach Plaid to start the connection. Please try again in a few minutes.');
                    onSettled();

                    return EMPTY;
                }),
            )),
        ).subscribe(({ rows, linkToken }) => this._openFreshPlaidLink(linkToken, rows, onSuccess, onSettled));
    }

    /**
     * Every row that was attached to the token being replaced, the clicked one included.
     *
     * ⚠️ THE CLICKED ROW IS ADDED UNCONDITIONALLY, as a premise guard. If the payload ever stopped
     * carrying `unifiedBankAccounts` this would otherwise sweep EMPTY — detaching nothing,
     * repointing nothing — and read exactly like a clean run on a single-account login.
     */
    private _rowsOnToken(oldToken: PlaidToken, clickedId: number, clickedLast4: string | null): FreshLinkRow[] {
        const rows: FreshLinkRow[] = (oldToken?.unifiedBankAccounts ?? [])
                .filter((uba: UnifiedBankAccount) => !!uba?.id)
                .map((uba: UnifiedBankAccount) => ({ id: uba.id, last4: uba.last4 || null }));

        if (!rows.some((row: FreshLinkRow) => row.id === clickedId)) {
            rows.push({ id: clickedId, last4: clickedLast4 });
        }

        return rows;
    }

    /**
     * Which account on the NEW login this row should be repointed at.
     *
     * ⚠️ NEVER A SILENT `plaidAccounts[0]` — review (Anthropic) A2. On a login carrying both an
     * operating and a reserve account, index 0 is a coin toss, and guessing wrong imports another
     * bank account's transactions and balance under this row's name while the growl says the
     * connection was restored. A fresh item mints new account ids, so the mask is the discriminator.
     *
     * The one unambiguous exception is a single row meeting a single new account: there is nothing
     * to confuse it with, and refusing it would break the ordinary one-account reconnect.
     */
    private _newAccountIdFor(row: FreshLinkRow, newToken: PlaidToken, rowCount: number): string | null {
        const accounts = newToken?.plaidAccounts ?? [];

        if (row.last4) {
            const byMask = accounts.filter((acct) => !!acct?.mask && acct.mask === row.last4);
            if (byMask.length === 1) {
                return byMask[0].accountId ?? null;
            }
        }

        if (rowCount === 1 && accounts.length === 1) {
            return accounts[0]?.accountId ?? null;
        }

        return null;
    }

    private _openFreshPlaidLink(linkToken: string, rows: FreshLinkRow[], onSuccess: () => void, onSettled: () => void): void {
        if (!linkToken) {
            this._growler.oops('We were unable to reach Plaid to start the connection. Please try again in a few minutes.');
            onSettled();

            return;
        }

        const plaidConfig = {
            onSuccess: (publicToken: any, metadata: PlaidSuccessMetaData) => {
                this.plaidObject.destroy();
                this.plaidObject = null;
                // ⚠️ CREATING THE TOKEN IS HALF THE JOB — THE ROWS MUST BE REPOINTED AT IT. They
                // were detached before the old token was destroyed, so until this lands they
                // render as unlinked, which is honest but not yet finished.
                this._plaidTokenService.createToken(publicToken, metadata.institution.institution_id).pipe(
                    switchMap((newToken: PlaidToken) => {
                        const assigned = rows
                                .map((row: FreshLinkRow) => ({ row, accountId: this._newAccountIdFor(row, newToken, rows.length) }))
                                .filter((pair) => !!pair.accountId);

                        if (!assigned.length) {
                            return of([]);
                        }

                        return forkJoin(assigned.map((pair) => this._bankAccounts.updateUnifiedAccount(pair.row.id, {
                            plaidTokenId: newToken.id,
                            plaidAccountId: pair.accountId,
                        })));
                    }),
                    // ⚠️ `finalize`, NOT `complete` — review F-8e1170, RE-RAISED because my first
                    // fix did not actually cover the failure it was about, and the comment I left
                    // here asserted that it did. RxJS terminates a subscription on an `error`
                    // notification WITHOUT ever emitting `complete`, so a `complete` arm releases
                    // the caller's in-flight guard on success only. When `createToken()` or
                    // `updateUnifiedAccount()` fails, the old token is already deleted and the rows
                    // already detached — exactly the moment the admin most needs the buttons — and
                    // every Link and Reconnect on the page stayed dead until a reload.
                    // `finalize` fires on complete, on error, and on unsubscribe.
                    finalize(() => onSettled()),
                ).subscribe({
                    next: (updated: unknown[]) => {
                        if (!updated.length) {
                            // Honest over wrong: the rows stay unlinked and keep their "Link" CTA
                            // rather than being pointed at a bank account we cannot identify.
                            this._growler.oops(
                                    'Your bank was connected, but we could not tell which account to link it to. Please link it from the account row.',
                            );
                        } else if (updated.length < rows.length) {
                            this._growler.oops(
                                    'Your bank was connected, but some accounts on that login still need linking. Please link them from their account rows.',
                            );
                        }
                        onSuccess();
                    },
                    error: () => this._growler.oops(
                            'Your bank was connected, but we could not finish linking it to this account. Please refresh and try again.',
                    ),
                });
            },
            onExit: (error: any, metadata: any) => {
                console.warn('Your Plaid Link Session ID was ' + metadata?.linkSessionId);
                console.warn('Please provide your Link Session ID to PayHOA Staff if requested.');
                this.plaidObject.destroy();
                this.plaidObject = null;
                // Review F-8e1170: abandoning the modal is the MOST likely ending, and it left the
                // page unusable. The rows were detached before the delete (A6), so they render as
                // "Link" rather than pointing at a token that no longer exists.
                onSettled();
                if (error !== null) {
                    this._growler.oops('An error occurred during the linking process. Please try again.');
                } else {
                    this._growler.oops('You must complete the Plaid link process to enable transactions. Please try again.');
                }
            },
            token: linkToken,
        };

        try {
            this.plaidObject = Plaid.create(plaidConfig);
            this.plaidObject.open();
        } catch (e) {
            // Same orphan cleanup as openPlaidLink(): create() can succeed and open() throw.
            try {
                this.plaidObject?.destroy();
            } catch {
                // intentionally swallowed — destroy() best-effort during cleanup
            }
            this.plaidObject = null;
            this._growler.oops('Could not open the Plaid connection window. Please try again.');
            onSettled();
        }
    }

    private openPlaidLink(token: PlaidToken, onSuccess: () => void): void {
        if (!token?.updateLinkToken) {
            this._growler.oops(
                    'We were unable to reach Plaid to start the reconnection. Please try again in a few minutes.',
            );
            return;
        }
        const plaidConfig = {
            onSuccess: (publicToken: any, metadata: PlaidSuccessMetaData) => {
                this.plaidObject.destroy();
                this.plaidObject = null;
                onSuccess();
            },
            onExit: (error: any, metadata: any) => {
                console.warn('Your Plaid Link Session ID was ' + metadata.linkSessionId);
                console.warn('Please provide your Link Session ID to PayHOA Staff if requested.');
                this.plaidObject.destroy();
                if (error !== null) {
                    // an error occurred in plaid
                    this._growler.oops('An error occurred during the linking process. Please try again.');
                } else {
                    // The user exited plaid
                    this._growler.oops('You must complete the Plaid link process to enable transactions. Please try again.');
                }
            },
            token: token.updateLinkToken,
        };

        try {
            this.plaidObject = Plaid.create(plaidConfig);
            this.plaidObject.open();
        } catch (e) {
            // If create() succeeded but open() threw, destroy the orphaned instance
            // before nulling so we don't leak Plaid SDK state.
            try {
                this.plaidObject?.destroy();
            } catch {
                // intentionally swallowed — destroy() best-effort during cleanup
            }
            this.plaidObject = null;
            this._growler.oops('Could not open the Plaid reconnection window. Please try again.');
        }
    }

    /**
     * @param {string} searchTerm
     * @param context
     * @param isRoutingNumber
     * @returns {Observable<Object>}
     */
    public searchInstitutions(
            searchTerm: string,
            context: 'auth' | 'transactions',
            isRoutingNumber = false,
    ): Observable<PlaidInstitutionSearchResult[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.PlaidSearch(context));

        return this._http.post(url, JSON.stringify({
            term: searchTerm,
            isRoutingNumber: isRoutingNumber,
        })).pipe(map((response: Object[]) => {
            return response.map(item => <PlaidInstitutionSearchResult>item);
        }));
    }

    public getLinkToken() {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.PlaidLinkToken());

        return this._http.post(url, JSON.stringify({})).pipe(map((res: any) => res.token));
    }
}
